import React from 'react';
import { motion } from 'framer-motion';
import { ErrorBoundary } from 'react-error-boundary';

interface TeaserErrorBoundaryProps {
  children: React.ReactNode;
  fallbackImageUrl?: string;
  featureName?: string;
}

const TeaserErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
  fallbackImageUrl?: string;
  featureName?: string;
}> = ({ error, resetErrorBoundary, fallbackImageUrl, featureName }) => {
  console.warn(`[TeaserDemo] Error in ${featureName}:`, error);
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center justify-center h-full bg-white/5 rounded-lg border border-white/10 p-4"
    >
      {fallbackImageUrl ? (
        <img 
          src={fallbackImageUrl}
          alt={`${featureName} preview`}
          className="w-full h-full object-cover rounded opacity-80"
        />
      ) : (
        <div className="text-center">
          <p className="text-white/70 text-sm mb-2">Feature preview unavailable</p>
          <button 
            onClick={resetErrorBoundary}
            className="text-blue-400 text-xs hover:text-blue-300 transition-colors"
          >
            Retry Preview
          </button>
        </div>
      )}
    </motion.div>
  );
};

export const TeaserErrorBoundary: React.FC<TeaserErrorBoundaryProps> = ({
  children,
  fallbackImageUrl,
  featureName
}) => (
  <ErrorBoundary
    FallbackComponent={(props) => (
      <TeaserErrorFallback 
        {...props} 
        fallbackImageUrl={fallbackImageUrl}
        featureName={featureName}
      />
    )}
    onError={(error) => {
      // Log to monitoring service if available
      console.error(`[TeaserDemo] ${featureName} component failed:`, error);
    }}
  >
    {children}
  </ErrorBoundary>
);